import * as React from "react"
import { format, parse, isValid } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"

import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"

/**
 * Browser-independent DatePicker component
 * Works consistently across Chrome, Firefox, Safari, and Edge
 */
function DatePicker({
  value,
  onChange,
  placeholder = "Select date",
  className,
  disabled = false,
  minDate,
  maxDate,
  dateFormat = "PPP", // e.g., "April 29, 2024"
}) {
  const [open, setOpen] = React.useState(false)

  // Parse the value to a Date object
  const selectedDate = React.useMemo(() => {
    if (!value) return undefined
    if (value instanceof Date) return value
    // Try parsing ISO string or common formats
    const parsed = new Date(value)
    return isValid(parsed) ? parsed : undefined
  }, [value])

  const handleSelect = (date) => {
    if (date) {
      // Return ISO string format for consistency
      onChange(date.toISOString().split('T')[0])
    } else {
      onChange('')
    }
    setOpen(false)
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal",
            !selectedDate && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {selectedDate ? format(selectedDate, dateFormat) : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleSelect}
          disabled={(date) => {
            if (minDate && date < new Date(minDate)) return true
            if (maxDate && date > new Date(maxDate)) return true
            return false
          }}
          initialFocus
        />
      </PopoverContent>
    </Popover>
  )
}

/**
 * Browser-independent MonthPicker component
 * For selecting month and year (e.g., for baseline periods)
 */
function MonthPicker({
  value,
  onChange,
  placeholder = "Select month",
  className,
  disabled = false,
  minDate,
  maxDate,
}) {
  const [open, setOpen] = React.useState(false)
  const [viewDate, setViewDate] = React.useState(() => {
    if (value) {
      const parsed = parse(value, 'yyyy-MM', new Date())
      return isValid(parsed) ? parsed : new Date()
    }
    return new Date()
  })

  // Parse the value (expected format: "2024-01" or "2024-01-01")
  const selectedMonth = React.useMemo(() => {
    if (!value) return null
    // Handle both "2024-01" and "2024-01-01" formats
    const dateStr = value.length === 7 ? `${value}-01` : value
    const parsed = new Date(dateStr)
    return isValid(parsed) ? { month: parsed.getMonth(), year: parsed.getFullYear() } : null
  }, [value])

  const months = [
    'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
  ]

  const handleMonthSelect = (monthIndex) => {
    const year = viewDate.getFullYear()
    const monthStr = `${year}-${String(monthIndex + 1).padStart(2, '0')}`
    onChange(monthStr)
    setOpen(false)
  }

  const handleYearChange = (delta) => {
    setViewDate(new Date(viewDate.getFullYear() + delta, viewDate.getMonth(), 1))
  }

  const isMonthDisabled = (monthIndex) => {
    const year = viewDate.getFullYear()
    const checkDate = new Date(year, monthIndex, 1)
    if (minDate) {
      const min = new Date(minDate)
      if (checkDate < new Date(min.getFullYear(), min.getMonth(), 1)) return true
    }
    if (maxDate) {
      const max = new Date(maxDate)
      if (checkDate > new Date(max.getFullYear(), max.getMonth(), 1)) return true
    }
    return false
  }

  const displayValue = React.useMemo(() => {
    if (!selectedMonth) return null
    return `${months[selectedMonth.month]} ${selectedMonth.year}`
  }, [selectedMonth])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal",
            !selectedMonth && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {displayValue || <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3" align="start">
        {/* Year selector */}
        <div className="flex items-center justify-between mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleYearChange(-1)}
            className="h-7 w-7 p-0"
          >
            <span className="sr-only">Previous year</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m15 18-6-6 6-6"/></svg>
          </Button>
          <span className="font-semibold">{viewDate.getFullYear()}</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleYearChange(1)}
            className="h-7 w-7 p-0"
          >
            <span className="sr-only">Next year</span>
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
          </Button>
        </div>
        {/* Month grid */}
        <div className="grid grid-cols-3 gap-2">
          {months.map((month, index) => {
            const isSelected = selectedMonth?.month === index &&
                              selectedMonth?.year === viewDate.getFullYear()
            const isDisabled = isMonthDisabled(index)
            return (
              <Button
                key={month}
                variant={isSelected ? "default" : "outline"}
                size="sm"
                disabled={isDisabled}
                onClick={() => handleMonthSelect(index)}
                className={cn(
                  "h-9",
                  isSelected && "bg-primary text-primary-foreground"
                )}
              >
                {month}
              </Button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}

/**
 * Browser-independent DateTimePicker component
 * For selecting both date and time
 */
function DateTimePicker({
  value,
  onChange,
  placeholder = "Select date and time",
  className,
  disabled = false,
  minDate,
  maxDate,
  showSeconds = false,
}) {
  const [open, setOpen] = React.useState(false)
  const [time, setTime] = React.useState({ hours: '00', minutes: '00', seconds: '00' })

  // Parse the value to a Date object
  const selectedDate = React.useMemo(() => {
    if (!value) return undefined
    const parsed = new Date(value)
    return isValid(parsed) ? parsed : undefined
  }, [value])

  // Update time state when value changes
  React.useEffect(() => {
    if (selectedDate) {
      setTime({
        hours: String(selectedDate.getHours()).padStart(2, '0'),
        minutes: String(selectedDate.getMinutes()).padStart(2, '0'),
        seconds: String(selectedDate.getSeconds()).padStart(2, '0'),
      })
    }
  }, [selectedDate])

  // The earliest instant this picker may emit, or null when unconstrained.
  // `minDate` arrives as a Date (ScheduleAudit), an ISO string (Meetings) and a
  // raw form value (PTW's work_end_date), so normalise it once here.
  // Deliberately not memoised on a stable key: callers pass `new Date()`, a
  // fresh object every render, and a stale "now" is the bug this guards.
  const minInstant = React.useMemo(() => {
    if (!minDate) return null
    const parsed = new Date(minDate)
    return isValid(parsed) ? parsed : null
  }, [minDate])

  // The minute dropdown below has no sub-minute option, so the smallest instant
  // the UI can actually represent is `minDate` rounded UP to the next minute.
  const minSelectable = React.useMemo(() => {
    if (!minInstant) return null
    const snapped = new Date(minInstant)
    if (snapped.getSeconds() || snapped.getMilliseconds()) {
      snapped.setSeconds(0, 0)
      snapped.setMinutes(snapped.getMinutes() + 1)
    }
    return snapped
  }, [minInstant])

  /**
   * Never emit an instant before `minDate`.
   *
   * The time selectors start at 00:00, so picking TODAY used to compose
   * today-at-local-midnight — always already in the past — and any API that
   * rejects past datetimes (POST /api/audit-schedules) turned an ordinary
   * "schedule it for today" into a 400. The calendar's own `disabled` predicate
   * constrains whole DAYS only, by design (today must stay selectable), so the
   * time half of the constraint has to be enforced here.
   */
  const clampToMin = (date) =>
    minSelectable && date < minSelectable ? new Date(minSelectable) : date

  const emit = (date) => {
    const finalDate = clampToMin(date)
    // Keep the dropdowns in step with what was actually emitted, so a clamp
    // reads as a correction the user can see rather than a silent rewrite.
    setTime({
      hours: String(finalDate.getHours()).padStart(2, '0'),
      minutes: String(finalDate.getMinutes()).padStart(2, '0'),
      seconds: String(finalDate.getSeconds()).padStart(2, '0'),
    })
    onChange(finalDate.toISOString())
  }

  // On the minimum day itself the earlier clock times are unreachable — grey
  // them out rather than letting the user pick one and snapping it forward.
  const startOfDay = (d) => {
    const copy = new Date(d)
    copy.setHours(0, 0, 0, 0)
    return copy.getTime()
  }
  const onMinDay = !!minSelectable && !!selectedDate &&
    startOfDay(selectedDate) === startOfDay(minSelectable)
  const minHour = onMinDay ? minSelectable.getHours() : null
  const minMinute = onMinDay ? minSelectable.getMinutes() : null

  const handleDateSelect = (date) => {
    if (date) {
      const newDate = new Date(date)
      newDate.setHours(parseInt(time.hours, 10))
      newDate.setMinutes(parseInt(time.minutes, 10))
      newDate.setSeconds(parseInt(time.seconds, 10))
      emit(newDate)
    }
  }

  const handleTimeChange = (field, value) => {
    const newTime = { ...time, [field]: value }
    setTime(newTime)

    if (selectedDate) {
      const newDate = new Date(selectedDate)
      newDate.setHours(parseInt(newTime.hours, 10))
      newDate.setMinutes(parseInt(newTime.minutes, 10))
      newDate.setSeconds(parseInt(newTime.seconds, 10))
      emit(newDate)
    }
  }

  const displayValue = React.useMemo(() => {
    if (!selectedDate) return null
    const dateStr = format(selectedDate, 'PPP')
    const timeStr = showSeconds
      ? `${time.hours}:${time.minutes}:${time.seconds}`
      : `${time.hours}:${time.minutes}`
    return `${dateStr} ${timeStr}`
  }, [selectedDate, time, showSeconds])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          disabled={disabled}
          className={cn(
            "w-full justify-start text-left font-normal",
            !selectedDate && "text-muted-foreground",
            className
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {displayValue || <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar
          mode="single"
          selected={selectedDate}
          onSelect={handleDateSelect}
          disabled={(date) => {
            if (minDate) {
              const minDay = new Date(minDate)
              minDay.setHours(0, 0, 0, 0)
              const checkDay = new Date(date)
              checkDay.setHours(0, 0, 0, 0)
              if (checkDay < minDay) return true
            }
            if (maxDate) {
              const maxDay = new Date(maxDate)
              maxDay.setHours(0, 0, 0, 0)
              const checkDay = new Date(date)
              checkDay.setHours(0, 0, 0, 0)
              if (checkDay > maxDay) return true
            }
            return false
          }}
          initialFocus
        />
        <div className="border-t p-3">
          <div className="flex items-center justify-center gap-2">
            <div className="flex items-center gap-1">
              <label className="text-sm text-muted-foreground">Time:</label>
              <select
                value={time.hours}
                onChange={(e) => handleTimeChange('hours', e.target.value)}
                className="h-9 w-14 rounded-md border border-input bg-background px-2 text-sm"
              >
                {Array.from({ length: 24 }, (_, i) => (
                  <option
                    key={i}
                    value={String(i).padStart(2, '0')}
                    disabled={minHour !== null && i < minHour}
                  >
                    {String(i).padStart(2, '0')}
                  </option>
                ))}
              </select>
              <span>:</span>
              <select
                value={time.minutes}
                onChange={(e) => handleTimeChange('minutes', e.target.value)}
                className="h-9 w-14 rounded-md border border-input bg-background px-2 text-sm"
              >
                {Array.from({ length: 60 }, (_, i) => (
                  <option
                    key={i}
                    value={String(i).padStart(2, '0')}
                    disabled={
                      minMinute !== null &&
                      parseInt(time.hours, 10) === minHour &&
                      i < minMinute
                    }
                  >
                    {String(i).padStart(2, '0')}
                  </option>
                ))}
              </select>
              {showSeconds && (
                <>
                  <span>:</span>
                  <select
                    value={time.seconds}
                    onChange={(e) => handleTimeChange('seconds', e.target.value)}
                    className="h-9 w-14 rounded-md border border-input bg-background px-2 text-sm"
                  >
                    {Array.from({ length: 60 }, (_, i) => (
                      <option key={i} value={String(i).padStart(2, '0')}>
                        {String(i).padStart(2, '0')}
                      </option>
                    ))}
                  </select>
                </>
              )}
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}

/**
 * Compact inline MonthPicker for use in tables/forms
 * Uses two select dropdowns (month + year) - works in all browsers
 */
function InlineMonthPicker({
  value,
  onChange,
  className,
  disabled = false,
  minYear = 2020,
  maxYear = new Date().getFullYear() + 1,
}) {
  // Parse the value (expected format: "2024-01")
  const [selectedYear, selectedMonth] = React.useMemo(() => {
    if (!value) return ['', '']
    const parts = value.split('-')
    return parts.length >= 2 ? [parts[0], parts[1]] : ['', '']
  }, [value])

  const months = [
    { value: '01', label: 'Jan' },
    { value: '02', label: 'Feb' },
    { value: '03', label: 'Mar' },
    { value: '04', label: 'Apr' },
    { value: '05', label: 'May' },
    { value: '06', label: 'Jun' },
    { value: '07', label: 'Jul' },
    { value: '08', label: 'Aug' },
    { value: '09', label: 'Sep' },
    { value: '10', label: 'Oct' },
    { value: '11', label: 'Nov' },
    { value: '12', label: 'Dec' },
  ]

  const years = React.useMemo(() => {
    const result = []
    for (let y = maxYear; y >= minYear; y--) {
      result.push(y.toString())
    }
    return result
  }, [minYear, maxYear])

  const handleMonthChange = (e) => {
    const newMonth = e.target.value
    if (selectedYear && newMonth) {
      onChange(`${selectedYear}-${newMonth}`)
    } else if (newMonth) {
      // Default to current year if no year selected
      onChange(`${new Date().getFullYear()}-${newMonth}`)
    }
  }

  const handleYearChange = (e) => {
    const newYear = e.target.value
    if (newYear && selectedMonth) {
      onChange(`${newYear}-${selectedMonth}`)
    } else if (newYear) {
      // Default to January if no month selected
      onChange(`${newYear}-01`)
    }
  }

  return (
    <div className={cn("flex gap-1", className)}>
      <select
        value={selectedMonth}
        onChange={handleMonthChange}
        disabled={disabled}
        className="px-2 py-1 border border-gray-300 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <option value="">Mon</option>
        {months.map(m => (
          <option key={m.value} value={m.value}>{m.label}</option>
        ))}
      </select>
      <select
        value={selectedYear}
        onChange={handleYearChange}
        disabled={disabled}
        className="px-2 py-1 border border-gray-300 rounded text-sm bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
      >
        <option value="">Year</option>
        {years.map(y => (
          <option key={y} value={y}>{y}</option>
        ))}
      </select>
    </div>
  )
}

export { DatePicker, MonthPicker, DateTimePicker, InlineMonthPicker }
